import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import RoleBasedHeader from '../../../components/ui/RoleBasedHeader';
import WelcomeBanner from '../../../components/welcome/WelcomeBanner';
import websocketService from '../../../utils/websocketService';
import KPICard from '../../role-based-dashboard/components/KPICard';
import ProjectCard from '../../role-based-dashboard/components/ProjectCard';
import QuickActions from '../../role-based-dashboard/components/QuickActions';
import TaskSummary from '../../role-based-dashboard/components/TaskSummary';
import TeamOverview from '../../role-based-dashboard/components/TeamOverview';

import sessionService from '../../../utils/sessionService.js';
import apiService from '../../../utils/apiService.js';
import realApiService from '../../../utils/realApiService.js';
import CreateProjectModal from '../../../components/modals/CreateProjectModal';
import CreateOrganizationModal from '../../../components/modals/CreateOrganizationModal';
import AdminPermissionsControl from '../../../components/settings/AdminPermissionsControl';
import InviteMemberModal from '../../../components/modals/InviteMemberModal';

import { listenForProjectUpdates } from '../../../utils/projectEventService';
import { useUserProfile } from '../../../hooks/useUserProfile';
import useToast from '../../../hooks/useToast';
import { useAuth } from '../../../contexts/AuthContext.jsx';

const OwnerDashboard = () => {
  const location = useLocation();
  const { getOrganizationId } = useAuth();

  // Use centralized user profile hook
  const {
    currentOrganization: hookCurrentOrganization,
  } = useUserProfile();

  // Toast notifications
  const { showToast } = useToast();

  const [searchValue, setSearchValue] = useState('');
  const [filterValue, setFilterValue] = useState('all');
  const [dashboardData, setDashboardData] = useState(null);
  // Removed kpiData state
  // const [activityFeed, setActivityFeed] = useState([]); // Removed unused
  const [projects, setProjects] = useState([]);
  const [recentTasks, setRecentTasks] = useState([]);
  const [taskCounts, setTaskCounts] = useState(null);
  // Removed calculatedPendingCount state
  const [currentUser, setCurrentUser] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [showCreateOrganization, setShowCreateOrganization] = useState(false);
  const [showInviteMember, setShowInviteMember] = useState(false);


  // Real team members data
  const [teamMembers, setTeamMembers] = useState([]);

  // Memoize organization ID to prevent recalculation
  const currentOrganizationId = useMemo(() => {
    // First try to get from AuthContext (most reliable)
    const authOrgId = getOrganizationId();
    if (authOrgId) return authOrgId;

    // Fallback to other sources
    return organizations?.[0]?.id || hookCurrentOrganization?.id;
  }, [getOrganizationId, organizations, hookCurrentOrganization]);

  // Helper to get current organization ID (now just returns memoized value)
  const getCurrentOrganizationId = useCallback(() => {
    return currentOrganizationId;
  }, [currentOrganizationId]);

  // Load dashboard data from backend - OPTIMIZED: Parallelized & Consolidated
  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Get basic organization context first
      const organizationId = sessionService.getOrganizationId();
      console.log('Owner Dashboard - Organization ID:', organizationId);

      if (!organizationId) {
        console.warn('No organization ID available');
        setProjects([]);
        setTeamMembers([]);
        setLoading(false);
        return;
      }

      console.log('🔄 OwnerDashboard: Parallel fetching all data...');
      
      // Parallelize all primary data fetches for a "lighter" and faster load
      const [userResult, projectsResult, dashboard] = await Promise.all([
        apiService.users.getCurrentUser(),
        apiService.projects.getAll(organizationId),
        realApiService.dashboardApi.getOwnerDashboard(organizationId)
      ]);

      // 1. Handle User & Organizations
      if (userResult) {
        setCurrentUser(userResult);
        if (userResult.organizations) {
          setOrganizations(userResult.organizations);
          console.log('✅ Organizations loaded:', userResult.organizations);
        }
      } else {
         // Fallback organizations
         setOrganizations([{
           id: organizationId,
           name: 'Organization',
           domain: 'example.com'
         }]);
      }

      // 2. Handle Projects
      console.log('Projects loaded:', projectsResult);
      setProjects(projectsResult || []);

      // 3. Handle Dashboard Stats & Team Data
      if (dashboard) {
        const stats = dashboard.stats || null;
        
        // Set dashboardData for KPI and TaskSummary access
        setDashboardData({
          ...dashboard,
          ...stats,
          my_tasks_stats: stats?.my_tasks_stats || null
        });
        
        // Set task counts from personal stats
        if (stats?.my_tasks_stats) {
          setTaskCounts({
            total: Number(stats.my_tasks_stats.total) || 0,
            todo: Number(stats.my_tasks_stats.todo) || 0,
            completed: Number(stats.my_tasks_stats.completed) || 0,
            inProgress: Number(stats.my_tasks_stats.in_progress) || 0,
            overdue: Number(stats.my_tasks_stats.overdue) || 0,
          });
        }

        // Map Recent Personal Tasks
        const personalTasks = dashboard.recent_personal_tasks || [];
        const mappedTasks = personalTasks.map((t) => {
          // Normalize status - handle various casing
          const rawStatus = (t.status || 'todo').toLowerCase();
          let normalizedStatus = rawStatus;
          if (rawStatus === 'in_progress' || rawStatus === 'in progress') normalizedStatus = 'In Progress';
          else if (rawStatus === 'review') normalizedStatus = 'Review';
          else if (rawStatus === 'blocked') normalizedStatus = 'Blocked';
          else if (rawStatus === 'done' || rawStatus === 'completed') normalizedStatus = 'Done';
          else if (rawStatus === 'todo' || rawStatus === 'to do' || rawStatus === 'to_do') normalizedStatus = 'To Do';
          else normalizedStatus = rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1);

          // Get project name from nested object or direct property
          const projectName = t.project?.name || t.project_name || null;

          return {
            id: t.id,
            title: t.title || t.name || 'Untitled',
            status: normalizedStatus,
            priority: t.priority || 'Medium',
            dueDate: t.due_date || t.dueDate || null,
            createdAt: t.created_at || t.assigned_at || t.updated_at || t.createdAt || t.updatedAt,
            assignee: t.assignee_name || t.assignee || (currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Me'),
            project: projectName,
            projectId: t.project?.id || t.project_id
          };
        });
        
        setRecentTasks(mappedTasks);
        
        // Calculate pending counts
        const pendingCount = mappedTasks.filter(t => 
          !['Done', 'Completed', 'done', 'completed'].includes((t.status || '').toLowerCase())
        ).length;


        // Map Team Members
        const activeMembers = dashboard.team_members || [];
        const pendingInvitations = dashboard.pending_invitations || [];
        
        const combinedTeam = [
          ...activeMembers.map(m => ({
            id: m.id,
            user_id: m.user_id,
            name: m.user ? `${m.user.first_name || ''} ${m.user.last_name || ''}`.trim() || m.user.email.split('@')[0] : 'Unknown',
            email: m.user?.email,
            role: m.role,
            status: 'active',
            avatar: '/assets/images/avatar.jpg',
            lastActivity: new Date(m.joined_at),
            joinedDate: new Date(m.joined_at),
            department: '',
            currentTask: '',
            tasksCompleted: 0
          })),
          ...pendingInvitations.map(invite => ({
            id: invite.id,
            name: invite.email.split('@')[0],
            email: invite.email,
            role: invite.role,
            status: 'pending',
            avatar: '/assets/images/avatar.jpg',
            lastActivity: new Date(invite.created_at || Date.now()),
            joinedDate: new Date(invite.created_at || Date.now()),
            department: '',
            currentTask: '',
            tasksCompleted: 0
          }))
        ];
        
        setTeamMembers(combinedTeam);
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      setError(error.message);
      // Clean up on major error
      setProjects([]);
      setTeamMembers([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once or in response to triggers

  // Handle welcome message once on mount
  useEffect(() => {
    if (location.state?.message && location.state?.type === 'success') {
      setWelcomeMessage(location.state.message);
      setShowWelcome(true);
      // Clear navigation state
      window.history.replaceState({}, document.title);
    }
  }, [location.state?.message, location.state?.type, setWelcomeMessage, setShowWelcome]); // Dependencies added to resolve ESLint warning

  // Initial dashboard load
  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Listen for real-time project updates - FIXED: Stable dependencies
  useEffect(() => {
    if (!currentOrganizationId) return;

    const unsubscribe = listenForProjectUpdates((updateData) => {
      console.log('🔄 Dashboard: Project update received, refreshing...', updateData);
      loadDashboardData();
    });

    const handleTaskUpdate = (data) => {
      console.log('🔄 Dashboard: Task update received, refreshing...', data);
      loadDashboardData();
    };

    websocketService.on('taskUpdated', handleTaskUpdate);
    websocketService.on('projectUpdated', loadDashboardData);

    return () => {
      unsubscribe();
      websocketService.off('taskUpdated', handleTaskUpdate);
      websocketService.off('projectUpdated', loadDashboardData);
    };
  }, [currentOrganizationId, loadDashboardData]); // FIXED: Use memoized organizationId

  // Owner-specific KPI data - FIXED: Memoized to prevent recalculation
  const ownerKPIData = useMemo(() => {
    const realData = dashboardData || {};
    const personalStats = realData.my_tasks_stats || {};
    
    const pendingCount = (personalStats.total || 0) - (personalStats.completed || 0);
    const completedCount = personalStats.completed || 0;
    const overdueCount = personalStats.overdue || 0;

    return [
      {
        title: 'My Projects',
        value: realData.total_projects?.toString() || '0',
        change: null,
        changeType: 'neutral',
        icon: 'FolderOpen',
        color: 'primary',
        changeLabel: null,
      },
      {
        title: 'Team Members',
        value: realData.total_members?.toString() || '0',
        change: null,
        changeType: 'neutral',
        icon: 'Users',
        color: 'primary',
        changeLabel: null,
      },
      {
        title: 'Pending Tasks',
        value: pendingCount.toString(),
        change: null,
        changeType: 'neutral',
        icon: 'ClipboardList',
        color: 'primary',
        changeLabel: null,
      },
      {
        title: 'Overdue',
        value: overdueCount.toString(),
        change: null,
        changeType: overdueCount > 0 ? 'negative' : 'neutral',
        icon: 'AlertCircle',
        color: 'error',
        changeLabel: null,
      },
      {
        title: 'Completed Tasks',
        value: completedCount.toString(),
        change: null,
        changeType: 'neutral',
        icon: 'CheckCircle',
        color: 'success',
        changeLabel: null,
      },
    ];
  }, [dashboardData]);

  // Filter projects based on search and filter criteria - FIXED: Memoized
  const filteredProjects = useMemo(() => {
    return projects.filter((project) => {
      const matchesSearch = project.name
        .toLowerCase()
        .includes(searchValue.toLowerCase());
      const matchesFilter =
        filterValue === 'all' || project.status === filterValue;
      return matchesSearch && matchesFilter;
    });
  }, [projects, searchValue, filterValue]);

  // Event handlers
  const handleOpenCreateProject = () => {
    setShowCreateProject(true);
  };

  const handleCloseCreateProject = () => {
    setShowCreateProject(false);
  };

  const handleCreateProject = async (projectData) => {
    try {
      const organizationId = getCurrentOrganizationId();
      if (!organizationId) {
        throw new Error('No organization found');
      }

      console.log('Creating project with:', {
        organizationId,
        projectData,
      });

      // Call the API with correct parameter order: (organizationId, projectData)
      const result = await apiService.projects.create(
        organizationId,
        projectData
      );

      console.log('Project creation result:', result);

      if (result) {
        // Add the new project to the current projects list immediately
        const newProject = {
          id: result.id,
          name: result.name || projectData.name,
          description: result.description || projectData.description,
          status: result.status || 'active',
          priority: result.priority || 'medium',
          created_at: result.created_at || new Date().toISOString(),
          updated_at: result.updated_at || new Date().toISOString(),
          organization_id: organizationId,
          created_by: result.created_by,
        };

        // Update projects list immediately
        setProjects((prevProjects) => [...(prevProjects || []), newProject]);

        // Also refresh from server to ensure consistency
        try {
          const projectsResult = await apiService.projects.getAll(
            organizationId
          );
          if (projectsResult && Array.isArray(projectsResult)) {
            setProjects(projectsResult);
          }
        } catch (refreshError) {
          console.warn('Failed to refresh projects list:', refreshError);
          // Keep the locally updated list if refresh fails
        }

        // Show success message
        console.log('✅ Project created successfully:', newProject.name);

        // Show success toast with team member notification info
        let toastMessage = `Project "${newProject.name}" created successfully!`;
        if (projectData.teamMembers && projectData.teamMembers.length > 0) {
          toastMessage += ` Team members have been notified.`;
        }
        if (projectData.projectManager) {
          toastMessage += ` Project manager has been assigned.`;
        }
        showToast(toastMessage, 'success', 6000);

        // Close the create project modal if it's open
        setShowCreateProject(false);
      }
    } catch (error) {
      console.error('Failed to create project:', error);

      // Enhanced error handling for permission issues
      if (
        error.message.includes('Permission denied') ||
        error.message.includes('create_project')
      ) {
        console.error('Permission Details:', {
          organizationId: getCurrentOrganizationId(),
          errorMessage: error.message,
        });

        // Provide user-friendly error message for owners
        throw new Error(
          'Only organization owners can create projects. Please ensure you have owner permissions in this organization.'
        );
      }
      throw error;
    }
  };

  const handleOpenCreateOrganization = () => {
    setShowCreateOrganization(true);
  };

  const handleCloseCreateOrganization = () => {
    setShowCreateOrganization(false);
  };

  const handleCreateOrganization = async (organizationData, logoFile) => {
    try {
      console.log('🚀 OWNER DASHBOARD: Creating organization:', organizationData);
      
      // Import apiService dynamically
      const { default: apiService } = await import('../../../utils/apiService');
      
      const result = await apiService.organizations.create(organizationData, logoFile);
      
      if (result.error) {
        throw new Error(result.error);
      }

      console.log('✅ OWNER DASHBOARD: Organization created successfully:', result.data);

      // Show success
      if (typeof showToast === 'function') {
        showToast(result.message || `Organization "${organizationData.name}" created successfully!`, 'success');
      }
      
      // Force Reload to refresh all data and contexts
      console.log('🔄 OWNER DASHBOARD: Reloading page...');
      window.location.reload();

      return result.data;
    } catch (error) {
      console.error('❌ OWNER DASHBOARD: Failed to create organization:', error);
      if (typeof showToast === 'function') {
        showToast(error.message || 'Failed to create organization', 'error');
      } else {
        showToast(error.message || 'Failed to create organization', 'error');
      }
    }
  };

  const handleManageUsers = () => {
    // Navigate to team members page
    window.location.href = '/team-members';
  };

  const handleInviteMembers = () => {
    setShowInviteMember(true);
  };

  const handleCloseInviteMember = () => {
    setShowInviteMember(false);
  };

  const handleSendInvitation = async (inviteData) => {
    try {
      const organizationId = getCurrentOrganizationId();
      if (!organizationId) {
        console.error('No organization selected');
        return;
      }

      console.log('Sending invitation:', inviteData);

      // Use realApiService for consistent API calls with credentials
      const result = await realApiService.organizations.inviteMember(organizationId, {
        email: inviteData.email,
        role: inviteData.role,
        invitation_message: inviteData.message || ''
      });

      console.log('Invitation sent successfully:', result);

      // Refresh dashboard stats to show updated member count
      const statsResult = await apiService.users.getDashboardStats();
      if (statsResult.data) {
        setDashboardData(statsResult.data);
      }
    } catch (error) {
      console.error('Failed to send invitation:', error);
      throw error;
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className='min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-900 flex items-center justify-center'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4'></div>
          <p className='text-slate-600 dark:text-slate-400'>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className='min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-900 flex items-center justify-center'>
        <div className='text-center max-w-md mx-auto p-6'>
          <div className='w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4'>
            <svg className='w-8 h-8 text-red-600 dark:text-red-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z' />
            </svg>
          </div>
          <h2 className='text-xl font-semibold text-gray-900 dark:text-slate-100 mb-2'>Dashboard Error</h2>
          <p className='text-gray-600 dark:text-slate-400 mb-6'>{error}</p>
          <button
            onClick={() => window.location.reload()}
            className='inline-flex items-center justify-center px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary-hover transition-colors duration-200'
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='aurora-container relative'>
      {/* Global Aurora Background Blobs (Inherited or explicitly defined for depth) */}
      <div className="aurora-blob top-0 -left-64 w-[700px] h-[700px] bg-blue-500/10 dark:bg-blue-600/5 transition-all duration-1000"></div>
      <div className="aurora-blob bottom-0 -right-64 w-[700px] h-[700px] bg-indigo-500/10 dark:bg-indigo-600/5 transition-all duration-1000"></div>
      
      <div className="relative animate-page-in pt-24">
      {/* Enhanced Welcome Banner for New Users */}
      <div className='px-10 relative z-10'>
        <WelcomeBanner />
      </div>

      {/* Legacy Welcome Message (fallback) */}
      {showWelcome && welcomeMessage && (
        <div className='glass-surface border-l-4 border-emerald-500 p-6 mb-6 mx-6 rounded-2xl shadow-xl animate-in'>
          <div className='flex items-center'>
            <div className='flex-shrink-0 w-10 h-10 bg-emerald-500/20 rounded-full flex items-center justify-center'>
              <svg
                className='h-6 w-6 text-emerald-500'
                viewBox='0 0 20 20'
                fill='currentColor'
              >
                <path
                  fillRule='evenodd'
                  d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                  clipRule='evenodd'
                />
              </svg>
            </div>
            <div className='ml-3'>
              <p className='text-sm text-green-700'>{welcomeMessage}</p>
            </div>
            <div className='ml-auto pl-3'>
              <button
                onClick={() => setShowWelcome(false)}
                className='text-green-400 hover:text-green-600'
              >
                <span className='sr-only'>Dismiss</span>
                <svg
                  className='h-5 w-5'
                  viewBox='0 0 20 20'
                  fill='currentColor'
                >
                  <path
                    fillRule='evenodd'
                    d='M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z'
                    clipRule='evenodd'
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <RoleBasedHeader
        userRole='owner'
        currentUser={currentUser}
        currentOrganization={organizations[0]}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        filterValue={filterValue}
        onFilterChange={setFilterValue}
      />

      {/* Dashboard content */}
      <div className='max-w-[1600px] mx-auto px-10 py-8'>
        {/* KPI Cards - Using personal stats aligned with Member dashboard */}
        <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6'>
          {loading ? (
            // Loading skeleton
            Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 animate-pulse">
                <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/2 mb-3"></div>
                <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-3/4 mb-3"></div>
                <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/3"></div>
              </div>
            ))
          ) : (
            ownerKPIData.map((kpi, index) => (
              <KPICard key={index} {...kpi} />
            ))
          )}
        </div>

        {/* Main Content Grid */}
        <div className='grid grid-cols-1 lg:grid-cols-4 gap-6 mb-6'>
          {/* Left Column - Projects */}
          <div className='lg:col-span-3'>
            {/* Projects Section with enhanced header */}
            <div className='space-y-4'>
              <div className='flex items-center justify-between'>
                <div>
                  <h2 className='text-2xl font-semibold text-slate-800 dark:text-slate-100 tracking-tight'>
                    My Organizations & Projects
                  </h2>
                  <p className='text-slate-600 dark:text-slate-400 mt-1'>
                    Manage and track your active projects across all
                    organizations
                  </p>
                </div>
                <div className='flex gap-3'>
                  <button
                    onClick={handleOpenCreateProject}
                    className='inline-flex items-center px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary-hover transition-colors duration-200 shadow-sm'
                  >
                    <svg
                      className='w-4 h-4 mr-2'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M12 4v16m8-8H4'
                      />
                    </svg>
                    New Project
                  </button>

                </div>
              </div>

              {/* Projects Grid - Scrollable Container */}
              <div className='h-[400px] overflow-y-auto overflow-x-visible pr-2 scrollbar-thin scrollbar-thumb-slate-200 dark:scrollbar-thumb-slate-700 scrollbar-track-transparent scrollbar-thumb-rounded-full hover:scrollbar-thumb-slate-300 dark:hover:scrollbar-thumb-slate-600 transition-colors'>
                <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 pb-32'>
                {filteredProjects.length > 0 ? (
                  filteredProjects.map((project) => (
                    <ProjectCard
                      key={project.id}
                      project={project}
                      userRole='owner'
                    />
                  ))
                ) : (
                  <div className='col-span-2 text-center py-12 bg-white dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700'>
                    <div className='w-16 h-16 mx-auto mb-4 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center'>
                      <svg
                        className='w-8 h-8 text-slate-400 dark:text-slate-500'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10'
                        />
                      </svg>
                    </div>
                    <h3 className='text-lg font-medium text-slate-900 dark:text-slate-100 mb-2'>
                      No projects yet
                    </h3>
                    <p className='text-slate-500 dark:text-slate-400 mb-6'>
                      Create your first project to get started with managing
                      your work.
                    </p>
                    <button
                      onClick={handleOpenCreateProject}
                      className='inline-flex items-center px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary-hover transition-colors duration-200'
                    >
                      <svg
                        className='w-4 h-4 mr-2'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M12 4v16m8-8H4'
                        />
                      </svg>
                      Create Project
                    </button>
                  </div>
                )}
              </div>
              </div>
            </div>
          </div>



          {/* Right Column - Quick Actions, Activity Feed, Notifications */}
          <div className='space-y-6 h-full'>
            <QuickActions
              userRole='owner'
              onCreateProject={handleOpenCreateProject}
              onCreateOrganization={handleOpenCreateOrganization}
              onManageUsers={handleManageUsers}
              onInviteMembers={handleInviteMembers}
              className="h-full"
            />
          </div>
        </div>

        {/* Bottom Section - Tasks, Team, and Admin Controls */}
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 items-stretch'>
          <div className="h-[500px]">
            <TaskSummary tasks={recentTasks} userRole='owner' counts={taskCounts} />
          </div>
          <div className="h-[500px]">
            <TeamOverview
              teamMembers={teamMembers}
              userRole='owner'
              onInviteMembers={handleInviteMembers}
            />
          </div>
        </div>

        {/* Admin Permissions Control - Owner Only */}
        <div className='mb-8'>
          <AdminPermissionsControl
            organizationId={getCurrentOrganizationId()}
            userRole='owner'
            onPermissionsChange={(updatedSettings) => {
              console.log('Admin permissions updated:', updatedSettings);
              // Optionally refresh dashboard data or show notification
            }}
          />
        </div>
      </div>

      {/* Create Project Modal */}
      <CreateProjectModal
        isOpen={showCreateProject}
        onClose={handleCloseCreateProject}
        onCreateProject={handleCreateProject}
        organizationId={getCurrentOrganizationId()}
        organizationName={organizations[0]?.name || 'Organization'}
      />



      {/* Create Organization Modal */}
      <CreateOrganizationModal
        isOpen={showCreateOrganization}
        onClose={handleCloseCreateOrganization}
        onCreateOrganization={handleCreateOrganization}
      />

      {/* Owner Invite Member Modal */}
      <InviteMemberModal
        isOpen={showInviteMember}
        onClose={handleCloseInviteMember}
        onInviteMember={handleSendInvitation}
        organizationId={getCurrentOrganizationId()}
        organizationName={organizations[0]?.name || 'Organization'}
      />
    </div>
    </div>
  );
};

export default OwnerDashboard;